// The few primitives every page uses, in the add-in's tokens. No UI kit.
import { useTranslation } from 'react-i18next';
import { Link, type LinkProps } from 'react-router-dom';
import { WEBSITE_URL } from '../config';
import type { ButtonHTMLAttributes, InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';

const FIELD = 'w-full rounded-md border border-line-btn bg-white px-3 py-2 text-[14px] text-ink placeholder:text-fainter focus:border-accent focus:outline-hidden focus:ring-0 disabled:bg-hover-soft disabled:text-muted';

export function Label({ htmlFor, children }: { htmlFor?: string; children: ReactNode }) {
  return (
    <label htmlFor={htmlFor} className="font-mono text-[11px] uppercase tracking-[0.06em] text-faint">
      {children}
    </label>
  );
}

/** An ⓘ next to a label: its text shows on hover, on focus (keyboard) and on tap (the tap focuses it), above the
 *  nearest positioned parent (the label row of a `Field`), aligned on its left. */
export function InfoTip({ label, children }: { label: string; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <span className="group inline-flex">
      <button type="button" aria-label={t('app.about', { label })} className="flex h-4 w-4 items-center justify-center rounded-full border border-line-btn text-[10px] leading-none text-faint hover:text-ink focus:text-ink focus:outline-hidden">
        i
      </button>
      <span role="tooltip" className="pointer-events-none absolute bottom-full left-0 z-20 mb-2 hidden w-72 max-w-full rounded-md border border-line-soft bg-white px-3 py-2 text-[12.5px] leading-[1.45] text-ink shadow-md group-focus-within:block group-hover:block">
        {children}
      </span>
    </span>
  );
}

export function Field({ id, label, hint, info, ...props }: InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; hint?: string; info?: string }) {
  return (
    <div className="flex w-full flex-col gap-1">
      {info ? (
        <div className="relative flex items-center gap-1.5">
          <Label htmlFor={id}>{label}</Label>
          <InfoTip label={label}>{info}</InfoTip>
        </div>
      ) : (
        <Label htmlFor={id}>{label}</Label>
      )}
      <input id={id} className={FIELD} {...props} />
      {hint && <span className="text-[12px] text-faint">{hint}</span>}
    </div>
  );
}

export function TextArea({ id, label, ...props }: TextareaHTMLAttributes<HTMLTextAreaElement> & { id: string; label: string }) {
  return (
    <div className="flex w-full flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <textarea id={id} className={FIELD} rows={3} {...props} />
    </div>
  );
}

export function Select({ id, label, children, ...props }: SelectHTMLAttributes<HTMLSelectElement> & { id: string; label: string; children: ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-1">
      <Label htmlFor={id}>{label}</Label>
      <select id={id} className={FIELD} {...props}>
        {children}
      </select>
    </div>
  );
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
const VARIANTS: Record<Variant, string> = {
  primary: 'bg-accent text-white hover:bg-accent-hover disabled:bg-send-idle disabled:text-muted',
  secondary: 'border border-line-btn bg-white text-ink hover:bg-hover disabled:opacity-60',
  ghost: 'text-muted hover:bg-hover hover:text-ink disabled:opacity-60',
  danger: 'border border-[#e8c9c5] bg-white text-danger hover:bg-danger-bg disabled:opacity-60',
};

export function Button({ variant = 'primary', className = '', ...props }: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return <button type="button" {...props} className={`rounded-[5px] px-3 py-2 text-[13px] font-medium transition-colors disabled:cursor-not-allowed ${VARIANTS[variant]} ${className}`} />;
}

/** A link that looks like a Button (a step that opens another page, such as the checkout). */
export function ButtonLink({ variant = 'primary', className = '', ...props }: LinkProps & { variant?: Variant }) {
  return <Link {...props} className={`inline-block rounded-[5px] px-3 py-2 text-[13px] font-medium transition-colors ${VARIANTS[variant]} ${className}`} />;
}

export function Alert({ kind = 'info', children }: { kind?: 'info' | 'error' | 'success' | 'warn'; children: ReactNode }) {
  const styles = {
    info: 'border-line-soft bg-white text-muted',
    error: 'border-[#e8c9c5] bg-danger-bg text-danger',
    success: 'border-line-soft bg-accent-bg text-accent-dark',
    warn: 'border-[#efd9a6] bg-warn-bg text-warn',
  }[kind];
  return (
    <div role={kind === 'error' ? 'alert' : 'status'} className={`rounded-md border px-3 py-2 text-[13px] leading-[1.45] ${styles} animate-pc-in`}>
      {children}
    </div>
  );
}

export function Card({ title, children, actions }: { title?: string; children: ReactNode; actions?: ReactNode }) {
  return (
    <section className="rounded-lg border border-line-card bg-white p-5 sm:p-6">
      {(title || actions) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          {title && <h2 className="text-[16px] font-medium tracking-[-0.01em] text-ink">{title}</h2>}
          {actions}
        </div>
      )}
      {children}
    </section>
  );
}

export function Spinner() {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-2 text-[13px] text-muted" role="status">
      <span className="inline-block h-3.5 w-3.5 animate-pc-spin rounded-full border-2 border-line border-t-accent" />
      {t('app.loading')}
    </div>
  );
}

/** The four-square Microsoft logo, inline so the login page needs no extra asset. */
export function MicrosoftGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}

export function MicrosoftButton({ href, label }: { href: string; label: string }) {
  return (
    <a href={href} data-testid="microsoft" className="flex items-center justify-center gap-2 rounded-[5px] border border-line-btn bg-white py-2 text-[13px] font-medium text-ink transition-colors hover:bg-hover">
      <MicrosoftGlyph />
      {label}
    </a>
  );
}

export function Divider({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-3 font-mono text-[10.5px] uppercase tracking-[0.06em] text-faint">
      <span className="h-px flex-1 bg-line-soft" />
      {label}
      <span className="h-px flex-1 bg-line-soft" />
    </div>
  );
}

export function Badge({ tone = 'neutral', children }: { tone?: 'neutral' | 'accent' | 'warn' | 'danger'; children: ReactNode }) {
  const styles = { neutral: 'bg-hover text-muted', accent: 'bg-accent-bg text-accent-dark', warn: 'bg-warn-bg text-warn', danger: 'bg-danger-bg text-danger' }[tone];
  return <span className={`inline-block rounded-sm px-1.5 py-0.5 font-mono text-[10.5px] uppercase tracking-wider ${styles}`}>{children}</span>;
}

export function LanguageToggle({ onChange }: { onChange: (locale: 'en' | 'fr') => void }) {
  const { i18n, t } = useTranslation();
  const next = i18n.language === 'fr' ? 'en' : 'fr';
  return (
    <button type="button" onClick={() => onChange(next)} className="h-7 rounded-[5px] px-2 font-mono text-[11px] text-muted hover:bg-hover-soft" title={t('app.language')}>
      {next.toUpperCase()}
    </button>
  );
}

/** The arrow back to the website, at the top left of every frame. */
export function BackToSite() {
  const { t } = useTranslation();
  return (
    <a href={WEBSITE_URL} className="inline-flex h-7 w-7 items-center justify-center rounded-[5px] text-muted hover:bg-hover-soft hover:text-ink" aria-label={t('app.backToSite')} title={t('app.backToSite')}>
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M13 8H3M7 4L3 8l4 4" />
      </svg>
    </a>
  );
}

/** Password input with a show / hide toggle. */
export function PasswordField({ id, label, show, onToggle, hint, ...props }: InputHTMLAttributes<HTMLInputElement> & { id: string; label: string; show: boolean; onToggle: () => void; hint?: string }) {
  const { t } = useTranslation();
  return (
    <div className="relative">
      <Field id={id} label={label} type={show ? 'text' : 'password'} hint={hint} {...props} />
      <button type="button" tabIndex={-1} onClick={onToggle} className="absolute right-2.5 top-[29px] font-mono text-[10.5px] text-faint hover:text-ink">
        {show ? t('auth.hide') : t('auth.show')}
      </button>
    </div>
  );
}
